import * as PropTypes from 'prop-types';

import { useTranslate } from '@tolgee/react';
import { useDispatch, useSelector } from 'react-redux';
import toast from 'react-hot-toast';

import { Dropdown } from 'react-bootstrap';
import { forwardRef } from 'react';

import { hideMessage } from '../../../../redux/slices/communication';

export default function MessageToolBar({
  left,
  editMessage,
  setShowDropdown,
  showDropdown,
  dropdownRef,
  data,
  setEditMessage,
  submitEditedMessage,
  deleteMessageBtn,
  availableActions = []
}) {
  const dispatch = useDispatch();
  const activeConversation = useSelector(
    (state) => state.communication.activeConversation
  );
  const CustomToggle = forwardRef(({ children, onClick }, ref) => (
    <div
      href=""
      className=" cursor-pointer me-1 px-2"
      ref={ref}
      onClick={(e) => {
        e.preventDefault();
        onClick(e);
      }}
    >
      {/* custom icon */}
      {children}
    </div>
  ));

  CustomToggle.displayName = 'CustomToggle';

  CustomToggle.propTypes = {
    children: PropTypes.node.isRequired,
    onClick: PropTypes.func.isRequired
  };
  const { t } = useTranslate();

  // Handler functions for actions
  const handleAction = (action) => {
    switch (action.type) {
      case 'edit':
        setEditMessage(true);
        break;
      case 'delete':
        deleteMessageBtn(data);
        break;
      case 'hide': {
        // Toggle hide state
        const newHiddenState = !data.is_hidden_on_platform;
        dispatch(
          hideMessage(
            data.uuid,
            activeConversation?.uuid,
            newHiddenState,
            (isHidden) => {
              // Success callback - show success toast
              const messageKey = isHidden
                ? 'communication.messages.message_hidden_successfully'
                : 'communication.messages.message_unhidden_successfully';
              toast.success(t(messageKey), {
                duration: 3000,
                position: 'top-right'
              });
            },
            (error) => {
              // Error callback - show error toast
              toast.error(
                t('communication.messages.message_hide_error'),
                {
                  duration: 5000,
                  position: 'top-right'
                }
              );
              console.error('Error hiding/unhiding message:', error);
            }
          )
        );
        break;
      }
      default:
        console.warn('Unknown action type:', action.type);
    }
    setShowDropdown(false);
  };

  return (
    <div className="pt-1 d-flex flex-end">
      <div className="block block-rounded block-transparent">
        <div className="block block-rounded block-transparent user-select-none">
          {!editMessage && availableActions.length > 0 && (
            <Dropdown
              show={showDropdown}
              onClick={() => setShowDropdown(!showDropdown)}
              onBlur={() => setShowDropdown(false)}
              ref={dropdownRef}
            >
              <Dropdown.Toggle as={CustomToggle}>{t('...')}</Dropdown.Toggle>
              <Dropdown.Menu show={showDropdown}>
                {availableActions.map((action, index) => (
                  <Dropdown.Item
                    key={index}
                    disabled={!action.enabled}
                    onClick={() => handleAction(action)}
                  >
                    <i className={`fa-duotone ${action.icon} cursor-pointer px-2`} />
                    <span>{t(action.labelKey)}</span>
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          )}
        </div>
        {editMessage && (
          <div className="d-flex align-items-center">
            <div
              className="badge badge-secondary me-1"
              onClick={() => setEditMessage(false)}
            >
              {t('communication.messageText.cancel')}
            </div>
            <div className="mx-1" />
            <div
              className="badge badge-primary me-1 cursor-pointer"
              onClick={submitEditedMessage}
            >
              {t('communication.messageText.save')}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

MessageToolBar.propTypes = {
  left: PropTypes.bool.isRequired,
  editMessage: PropTypes.bool.isRequired,
  setShowDropdown: PropTypes.func.isRequired,
  showDropdown: PropTypes.bool.isRequired,
  dropdownRef: PropTypes.object.isRequired,
  data: PropTypes.object.isRequired,
  setEditMessage: PropTypes.func.isRequired,
  submitEditedMessage: PropTypes.func.isRequired,
  deleteMessageBtn: PropTypes.func.isRequired,
  availableActions: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string.isRequired,
      labelKey: PropTypes.string.isRequired,
      icon: PropTypes.string.isRequired,
      enabled: PropTypes.bool.isRequired
    })
  ).isRequired
};
